import React, { FC, useEffect, useState } from 'react';
import { Grid, Box, IconButton, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper } from '@mui/material';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { useAppDispatch } from '@hooks/redux-custom';

import WebhookForm from '../WebhookForm/WebhookForm';
import { useCreateWebhookMutation, useDeleteWebhookMutation } from '@services';

import { addErrorToLog } from '@store/slices/error-log';
import { showToast } from '@store/slices/notification';

import { JimboResponse, WebhookEndpoints } from '@models';
import { LoadingSpinner, SubTitle } from '@components';
import { useAppSelector } from '@hooks/redux-custom';

type Props = {
  title: string;
  moduleName: string;
  initialLists: WebhookEndpoints[] | [];
  testId: string;
  refetch: () => void;
};

const WebhookList: FC<Props> = ({ title, moduleName, initialLists, testId, refetch }) => {
  const [deleteWebhook, { isLoading: deleteWebhookLoading }] = useDeleteWebhookMutation();
  const [createWebhook, { isLoading }] = useCreateWebhookMutation();

  const [customURLs, setCustomURLs] = useState<WebhookEndpoints[]>([]);
  const dispatch = useAppDispatch();

  const activeUser = useAppSelector((state) => state.activeUser.value);

  const userAccessStatus = activeUser?.user?.roles.find((role: any) => role.moduleName === 'webhook');
  const hasWriteAccess = userAccessStatus.role > 1;

  const onSave = async (formData: WebhookEndpoints) => {
    const duplicateURL = customURLs.find((customUrl) => customUrl?.url === formData.url);

    if (duplicateURL) {
      dispatch(
        showToast({
          message: `URL already exists`,
          type: 'error',
        }),
      );

      return;
    }

    const data = await createWebhook({
      ...formData,
      module: moduleName,
    })
      .unwrap()
      .catch((error: { data: JimboResponse }) => {
        dispatch(addErrorToLog(error?.data));

        dispatch(
          showToast({
            message: `Failed to create the webhook !`,
            type: 'error',
          }),
        );
      });

    if (data) {
      dispatch(
        showToast({
          message: `Successfully created new webhook for ${data.url}!`,
          type: 'success',
        }),
      );

      const newCustomURLs = [...customURLs, data];

      setCustomURLs(newCustomURLs);
      refetch();
    }
  };

  const deleteURL = async (url: string) => {
    const selectedHook: WebhookEndpoints = customURLs.find((webhookData: WebhookEndpoints) => webhookData.url === url) ?? {
      url: '',
    };

    if (selectedHook?.id) {
      const data = await deleteWebhook(selectedHook?.id)
        .unwrap()
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          dispatch(
            showToast({
              message: `Failed to delete the webhook !`,
              type: 'error',
            }),
          );
        });

      if (data?.code === 200) {
        dispatch(
          showToast({
            message: `Successfully deleted the webhook !`,
            type: 'success',
          }),
        );

        refetch();

        setCustomURLs(customURLs.filter((webhookData: WebhookEndpoints) => webhookData.url !== url));
      }
    }
  };

  useEffect(() => {
    if (initialLists.length > 0) {
      setCustomURLs(initialLists);
    }
  }, [initialLists]);

  if (deleteWebhookLoading || isLoading) return <LoadingSpinner isOpen={deleteWebhookLoading || isLoading} />;

  return (
    <>
      <SubTitle title={title} />
      <Box p={2}>
        {hasWriteAccess && (
          <Grid container spacing={2} alignItems="center" paddingBottom={5} data-testid={testId}>
            <Grid item xs={12}>
              <WebhookForm title={title} onSave={onSave}></WebhookForm>
            </Grid>
          </Grid>
        )}
        {customURLs.length > 0 && (
          <TableContainer component={Paper} data-testid={`${testId}-table`}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>URL</TableCell>
                  <TableCell>Description</TableCell>
                  {hasWriteAccess && <TableCell>Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {customURLs.map((customURL, index) => (
                  <TableRow key={customURL.url + index}>
                    <TableCell>{customURL.url}</TableCell>
                    <TableCell>{customURL.description}</TableCell>
                    <TableCell>
                      {hasWriteAccess && (
                        <IconButton onClick={() => deleteURL(customURL.url)} data-testid={`webhook-delete-btn-${customURL.url}`}>
                          <DeleteOutline />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </>
  );
};

export default WebhookList;
